import { useEffect, useRef, useState } from "react";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";

interface UseTextNotesSignedScreenshotReturn {
  signedScreenshotUrl: string;
  loading: boolean;
  error: string | null;
}

export const useTextNotesSignedScreenshot = (
  identifier: string | null,
): UseTextNotesSignedScreenshotReturn => {
  const [signedScreenshotUrl, setSignedScreenshotUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    // Reset mounted flag on effect run
    isMountedRef.current = true;

    const fetchScreenshot = async () => {
      if (!identifier) {
        // Only update state if still mounted
        if (isMountedRef.current) {
          setSignedScreenshotUrl("");
          setLoading(false);
          setError(null);
        }
        return;
      }

      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const filePath = `onco_emr/${identifier}/onco_emr_text_note_screenshot/text_notes_sign.png`;
        const downloadUrl = await getFileDownloadUrl(filePath);

        // Only update state if component is still mounted and identifier hasn't changed
        if (isMountedRef.current) {
          if (downloadUrl) {
            setSignedScreenshotUrl(downloadUrl);
            setError(null);
          } else {
            setError("Failed to fetch screenshot URL");
            setSignedScreenshotUrl("");
          }
        }
      } catch (err) {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          console.error("Error fetching text note screenshot:", err);
          setError("Error fetching screenshot");
          setSignedScreenshotUrl("");
        }
      } finally {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchScreenshot();

    // Cleanup function to prevent state updates after unmount or identifier change
    return () => {
      isMountedRef.current = false;
    };
  }, [identifier]);

  return { signedScreenshotUrl, loading, error };
};
